import { createSelector } from 'reselect';
import { isEmpty, intersection, values, map, uniqBy, find, get } from 'lodash';
import { getQueryParam } from 'utils/query';

export const getOrganizationMembers = (state) => state.organization.members.data;
export const getOrganizationMembersLoading = (state) => state.organization.members.isLoading;
export const getOrganizationProjects = (state) => state.organization.projects.data;
export const getOrganizationProjectsLoading = (state) => state.organization.projects.isLoading;
export const getOrganizationTeams = (state) => state.organization.teams.data;
export const getPermissions = (state) => state.organization.permissions.data;
export const getOrganizationRoles = (state) => state.organization.roles.data;
export const getOrganizationRolesLoading = (state) => state.organization.roles.isLoading;
export const getOrganizationProjectById = (state, props) =>
  state.organization.projects.data[props.projectId];
export const selectOrganizationPermissions = createSelector([getPermissions], (permissions) =>
  values(permissions).filter((p) => p.scope === 'Organization'),
);
export const selectOrganizationPermissionsForForm = createSelector(
  [selectOrganizationPermissions],
  (permissions) => permissions.filter((p) => p.name !== 'Manage Organization'),
);

const getFilters = (state, props) => ({
  userId: props.userId || getQueryParam(props, 'userId'),
  search: get(props.filters, 'search'),
  status: get(props.filters, 'status'),
  teamRoles: get(props.filters, 'teamRoles'),
  projectRoles: get(props.filters, 'projectRoles'),
  teams: get(props.filters, 'teams'),
  projects: get(props.filters, 'projects'),
  memberSearch: get(props.filters, 'memberSearch'),
});

const mergeProjectAndTeamRoles = (roles, pm, member) => {
  const projectRole = get(roles[pm.roleId], 'name', '');
  const teamRoleId = get(
    member.teamMemberships.find((tm) => tm.projectId === pm.projectId),
    'roleId',
  );
  const teamRole = get(roles[teamRoleId], 'name', '');
  return `${projectRole}, ${teamRole}`;
};

export const selectOrganizationProjects = createSelector([getOrganizationProjects], (projects) =>
  values(projects),
);

export const selectOrganizationMembers = createSelector(
  [
    getOrganizationProjects,
    getOrganizationTeams,
    getOrganizationRoles,
    getOrganizationMembers,
    getFilters,
    selectOrganizationPermissionsForForm,
  ],

  (projects, teams, roles, members, filters, organizationPermissionsForForm) => {
    const result = values(members)
      .filter((member) => {
        if (filters.userId) {
          return member.userId === filters.userId;
        }

        if (!isEmpty(filters.memberSearch)) {
          const search = filters.memberSearch[0].toLowerCase();
          if (
            !member.email.toLowerCase().includes(search) &&
            !`${member.firstName} ${member.lastName}`.toLowerCase().includes(search)
          ) {
            return false;
          }
        }

        if (!isEmpty(filters.teams)) {
          const memberTeamIds = map(member.teamMemberships, 'teamId');
          if (isEmpty(intersection(filters.teams, memberTeamIds))) {
            return false;
          }
        }

        if (!isEmpty(filters.teamRoles)) {
          const memberRoleIds = map(member.teamMemberships, 'roleId');
          if (isEmpty(intersection(filters.teamRoles, memberRoleIds))) {
            return false;
          }
        }

        if (!isEmpty(filters.projectRoles)) {
          const memberRoleIds = map(member.projectMemberships, 'roleId');
          if (isEmpty(intersection(filters.projectRoles, memberRoleIds))) {
            return false;
          }
        }

        if (!isEmpty(filters.projects)) {
          const memberProjectIds = map(member.projectMemberships, 'projectId');
          if (isEmpty(intersection(filters.projects, memberProjectIds))) {
            return false;
          }
        }

        return true;
      })

      .map((member) => ({
        ...member,
        id: member.userId,
        fullName: `${member.firstName} ${member.lastName}`,
        organizationPermissionsForDisplay: member.organizationPermissions.map((id) =>
          find(organizationPermissionsForForm, { id }),
        ),
        projects: uniqBy(member.projectMemberships, 'projectId').map((r) => projects[r.projectId]),
        teams: uniqBy(member.teamMemberships, 'teamId').map((r) => teams[r.teamId]),
        roles: uniqBy(member.teamMemberships.concat(member.projectMemberships), 'roleId').map(
          (r) => roles[r.roleId],
        ),
        projectsWithRoles: uniqBy(member.projectMemberships, 'projectId').map((r) => ({
          project: get(projects[r.projectId], 'name'),
          name: get(roles[r.roleId], 'name'),
        })),
        teamsWithRoles: uniqBy(member.teamMemberships, 'projectId').map((r) => ({
          project: get(projects[r.projectId], 'name'),
          team: get(teams[r.teamId], 'name'),
          name: get(roles[r.roleId], 'name'),
        })),
        projectAndTeamRoles: uniqBy(member.projectMemberships, 'projectId').map((pm) => ({
          project: get(projects[pm.projectId], 'name'),
          name: mergeProjectAndTeamRoles(roles, pm, member),
        })),
      }));
    return result;
  },
);

export const selectRoles = createSelector(
  [getOrganizationRoles, getPermissions],
  (roles, allPermissions) =>
    values(roles).map((role) => ({
      ...role,
      permissions: role.permissions.map((permissionId) => allPermissions[permissionId]),
    })),
);

export const selectProjectRoles = createSelector(
  [selectRoles, getOrganizationMembers],
  (roles, members) =>
    roles
      .filter((role) => role.scope === 'Project')
      .map((role) => ({
        ...role,
        memberCount: values(members).filter(
          (m) => values(m.projectMemberships).filter((pm) => pm.roleId === role.id).length,
        ).length,
      })),
);

export const selectTeamRoles = createSelector(
  [selectRoles, getOrganizationMembers],
  (roles, members) =>
    roles
      .filter((role) => role.scope === 'Team')
      .map((role) => ({
        ...role,
        memberCount: values(members).filter(
          (m) => values(m.teamMemberships).filter((tm) => tm.roleId === role.id).length,
        ).length,
      })),
);

export const selectTeamRolesNamesAndPermissionsMap = createSelector(
  [selectTeamRoles],
  (teamRoles) =>
    teamRoles.reduce((acc, teamRole) => {
      acc[teamRole.name] = teamRole.permissions;
      return acc;
    }, {}),
);

export const selectCombinedRoles = createSelector(
  [selectProjectRoles, selectTeamRolesNamesAndPermissionsMap],
  (projectRoles, teamRoleNameAndPermissions) =>
    projectRoles
      .filter((projectRole) => !!teamRoleNameAndPermissions[projectRole.name])
      .map((pr) => ({
        ...pr,
        scope: 'ProjectTeam',
        permissions: pr.permissions.concat(teamRoleNameAndPermissions[pr.name]),
      })),
);
